import { loadLanguagesAsync } from '@/i18n'

export class LayoutButtonDef {
  key
  identifier
  title
  action
  function
  link
  style
  icon
  form
  popconfirm = false
  loading = false
  forceTitle = false
  iconOnly = false
  items = []
  type
}

export class LayoutButtonFactory {
  button

  constructor () {
    this.button = new LayoutButtonDef()
  }

  /**
   * @param {string} key
   * @returns {LayoutButtonFactory}
   */
  key (key) {
    this.button.key = key
    return this
  }

  /**
   * @param {string} identifier
   * @returns {LayoutButtonFactory}
   */
  identifier (identifier) {
    this.button.identifier = identifier
    return this
  }

  /**
   * @param {string} title
   * @returns {LayoutButtonFactory}
   */
  title (title) {
    this.button.title = title
    return this
  }

  /**
   * @param {string|null} action
   * @returns {LayoutButtonFactory}
   */
  action (action) {
    this.button.action = action
    return this
  }

  /**
   * @param {function|null} functionDef
   * @returns {LayoutButtonFactory}
   */
  function (functionDef) {
    this.button.function = functionDef
    return this
  }

  /**
   * @param {string} link
   * @returns {LayoutButtonFactory}
   */
  link (link) {
    this.button.link = link
    return this
  }

  /**
   * @param {string} style
   * @returns {LayoutButtonFactory}
   */
  style (style) {
    this.button.style = style
    return this
  }

  /**
   * @param {string} icon
   * @returns {LayoutButtonFactory}
   */
  icon (icon) {
    this.button.icon = icon
    return this
  }

  /**
   * @param {string} form
   * @returns {LayoutButtonFactory}
   */
  form (form) {
    this.button.form = form
    return this
  }

  /**
   * @param {boolean} confirm
   * @returns {LayoutButtonFactory}
   */
  popconfirm (confirm) {
    this.button.popconfirm = confirm
    return this
  }

  /**
   * @param {boolean} loading
   * @returns {LayoutButtonFactory}
   */
  loading (loading) {
    this.button.loading = loading
    return this
  }

  /**
   * @param {boolean} forceTitle
   * @returns {LayoutButtonFactory}
   */
  forceTitle (forceTitle = true) {
    this.button.forceTitle = forceTitle
    return this
  }

  /**
   * @param {boolean} iconOnly
   * @returns {LayoutButtonFactory}
   */
  iconOnly (iconOnly = true) {
    this.button.iconOnly = iconOnly
    return this
  }

  options (options) {
    this.button.options = options
    return this
  }

  type (type) {
    this.button.type = type
    return this
  }

  add (button) {
    this.button.items.push(button)
    return this
  }

  /**
   * @returns {LayoutButtonDef}
   */
  create () {
    this.button.items = this.button.items.map(button => button instanceof LayoutButtonFactory ? button.create() : button)
    return this.button
  }
}

export class LayoutFactory {
  button () {
    return new LayoutButtonFactory()
  }
}

export const layoutFactory = () => {
  return new LayoutFactory()
}

/**
 * @returns {LayoutButtonFactory}
 */
export const button = () => {
  return new LayoutButtonFactory()
}

export const makeDefaultButton = (options, func = null) => {
  return button()
    .title(options.title)
    .link(options.link)
    .style(options.style)
    .icon(options.icon)
    .options(options.options)
    .action('function')
    .function(func)
}

export const makeButtonByDefaultIdentifier = (defaultIdentifier) => {
  switch (defaultIdentifier) {
    case 'create': return makeCreateButton()
    case 'update':
    case 'save': return makeSaveButton()
    case 'delete': return makeDeleteButton()
    case 'edit': return makeEditButton()
    case 'continue': return makeContinueButton()
  }
}

export const makeSaveButton = (options, func) => {
  options = options || {}
  return makeDefaultButton(options, func)
    .title(options.title || '_.save')
    .type('save')
    .style(options.style || 'primary')
    .icon(options.icon || 'save')
    .action(func ? 'function' : null)
}

export const makeEditButton = (options, func) => {
  options = options || {}
  return makeDefaultButton(options, func)
    .title(options.title || '_.edit')
    .type('edit')
    .style(options.style || 'primary')
    .icon(options.icon || 'edit')
    .action(null)
}

export const makeDeleteButton = (options, func) => {
  options = options || {}
  return makeDefaultButton(options, func)
    .title(options.title || '_.delete')
    .type('delete')
    .style(options.style || 'danger')
    .icon(options.icon || 'delete')
    .action(func ? 'function' : 'delete')
    .popconfirm(true)
}

export const makeCreateButton = (options, func) => {
  options = options || {}
  return makeDefaultButton(options, func)
    .title(options.title || '_.create')
    .type('create')
    .style(options.style || 'add')
    .icon(options.icon || 'plus')
}

export const makeContinueButton = (options, func) => {
  options = options || {}
  return makeDefaultButton(options, func)
    .title(options.title || '_.save_and_continue')
    .type('continue')
    .style(options.style || 'default')
    .icon(options.icon || 'check-circle')
}

export const makeMoreButton = () => {
  return layoutFactory().button()
    .identifier('more')
    .iconOnly()
    .icon('more')
    .type('more')
    .title('_.more')
    .style('ghost')
}

export const makeReloadTranslationsButton = () => {
  return layoutFactory().button()
    .title('bakery.dev.buttons.reload_translations')
    .type('translations')
    .function(loadLanguagesAsync)
    .style('dashed')
    .icon('global')
}
